import { ContentService } from "../../../services/content.service.js";
import { locale } from "../../../services/LocaleService.js";

/**
 * CommentSection Component
 * Encapsulates all comment rendering and interaction logic.
 * 
 * @param {HTMLElement} container - The element to render the comment section into.
 * @param {Object} props - Properties { seriesId, episodeId, currentUser, authorId, targetCommentId }
 */
export const CommentSection = (container, { seriesId, episodeId, currentUser, authorId, hasNext, hasPrev, onNext, onPrev, targetCommentId }) => {
    // 1. Safe ID Handling
    const safeSeriesId = String(seriesId);
    const safeEpId = String(episodeId);
    const user = currentUser;
    const userPhoto = user?.photoURL || null;

    // UI Template
    container.innerHTML = `
        <div class="max-w-2xl mx-auto px-4 py-8 relative font-sans">
            <!-- Navigation Buttons (Removed) -->

            <h3 class="text-lg font-bold mb-4 flex items-center gap-2 text-gray-200">
                댓글 <span id="comment-count" class="text-sm font-normal text-gray-400">(0)</span>
            </h3>
            
            <!-- Input -->
            <div class="flex gap-3 mb-8">
                <div class="w-10 h-10 rounded-full bg-zinc-700 flex-shrink-0 flex items-center justify-center overflow-hidden border border-zinc-600">
                    ${userPhoto ?
            `<img src="${userPhoto}" class="w-full h-full object-cover" alt="User">` :
            `<span class="material-symbols-outlined text-gray-400">person</span>`
        }
                </div>
                <div class="flex-grow relative">
                    <textarea id="comment-input" class="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-sm focus:outline-none focus:border-blue-500 focus:bg-zinc-800 transition-colors resize-none text-gray-200 placeholder-zinc-500 min-h-[50px]" rows="1" placeholder="댓글을 남겨주세요..." disabled></textarea>
                    
                    <button id="btn-submit-comment" class="absolute right-2 top-1/2 -translate-y-1/2 bg-blue-600 text-white p-1.5 px-3 rounded-lg text-xs font-bold shadow-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all">등록</button>
                    
                    ${!user ? `<div class="absolute inset-0 bg-black/50 flex items-center justify-center text-sm text-gray-400 cursor-pointer backdrop-blur-[1px] rounded-xl" id="login-overlay">${locale.t('auth.login_required')}</div>` : ''}
                </div>
            </div>

            <!-- List -->
            <div id="comment-list" class="space-y-6">
                <div class="text-center py-10 opacity-50"><span class="loader-spin inline-block w-6 h-6 border-zinc-500 border-t-blue-500"></span></div>
            </div>
        </div>
    `;

    // Navigation Buttons Removed (Duplicate of Viewer Navigation)

    // Handle Login Overlay
    const loginOverlay = container.querySelector('#login-overlay');
    if (loginOverlay) {
        loginOverlay.onclick = async () => {
            if (await window.Modal.confirm(locale.t('msg.login_required'), locale.t('menu.login'))) {
                window.router.navigate('/login');
            }
        };
    }

    const input = container.querySelector('#comment-input');
    const submitBtn = container.querySelector('#btn-submit-comment');

    if (user) {
        input.disabled = false;
        input.oninput = function () {
            this.style.height = 'auto';
            this.style.height = (this.scrollHeight) + 'px';
        };
    }

    // Function to load comments
    const loadComments = async () => {
        const listEl = container.querySelector('#comment-list');
        const countEl = container.querySelector('#comment-count');
        if (!listEl) return;

        try {
            // [FIX] Handle Pagination Object ({ items, lastVisible })
            const { items } = await ContentService.getComments(safeSeriesId, safeEpId);
            const allComments = items || [];

            countEl.innerText = `(${allComments.length})`;

            if (allComments.length === 0) {
                listEl.innerHTML = `<div class="text-center py-10 text-gray-500 text-sm">${locale.t('guestbook.empty')}</div>`;
                return;
            }

            listEl.innerHTML = '';

            // Render
            allComments.forEach(comment => {
                listEl.appendChild(createCommentNode(comment));
            });

            // [Deep Link] Scroll & Highlight Logc
            if (targetCommentId) {
                setTimeout(() => {
                    const targetEl = listEl.querySelector(`div[data-id="${targetCommentId}"]`);
                    if (targetEl) {
                        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

                        // Visual Highlight Effect
                        targetEl.classList.add('ring-2', 'ring-blue-500', 'bg-blue-500/10', 'transition-all', 'duration-500');

                        // Remove highlight after 3 seconds
                        setTimeout(() => {
                            targetEl.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-500/10');
                            targetEl.classList.add('transition-colors', 'duration-1000'); // Slow fade out
                        }, 3000);
                    }
                }, 100); // Small delay to ensure render
            }

        } catch (e) {
            console.error("Failed to load comments:", e);
            listEl.innerHTML = '<div class="text-center py-4 text-red-500 text-xs">댓글을 불러오는데 실패했습니다.</div>';
        }
    };

    // Helper: Create Comment Node
    const createCommentNode = (data) => {
        const isMyComment = user && user.uid === data.userId;
        const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
        const isSeriesAuthor = user && (authorId && user.uid === authorId);

        // Permissions
        const canEdit = isMyComment;
        const canDelete = isMyComment || isAdmin || isSeriesAuthor;

        const date = data.createdAt ? new Date(data.createdAt.toDate()).toLocaleDateString() + ' ' + new Date(data.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '방금 전';

        const avatarHtml = data.photoURL ?
            `<img src="${data.photoURL}" class="w-full h-full object-cover">` :
            `<div class="w-full h-full flex items-center justify-center bg-indigo-900 text-indigo-300 font-bold text-xs uppercase">${data.userEmail.slice(0, 2)}</div>`;

        const outerDiv = document.createElement('div');
        outerDiv.className = 'flex flex-col mb-4 comment-item-container fade-in';
        outerDiv.setAttribute('data-id', data.id);

        outerDiv.innerHTML = `
            <div class="flex gap-3 group relative">
                <div class="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 shadow-sm border border-zinc-700 cursor-pointer hover:opacity-80 transition-opacity" onclick="event.stopPropagation(); window.router.navigate('/user/${data.userId}')">
                    ${avatarHtml}
                </div>
                <div class="flex-grow min-w-0">
                    <div class="flex justify-between items-start">
                        <div class="flex flex-col">
                            <span class="text-xs font-bold text-gray-300 flex items-center gap-2 cursor-pointer hover:text-white transition-colors" onclick="event.stopPropagation(); window.router.navigate('/user/${data.userId}')">
                                ${data.displayName || data.userEmail.split('@')[0]} 
                                ${data.role === 'author' ? '<span class="bg-blue-900/50 text-blue-300 px-1.5 py-0.5 rounded text-[10px]">Author</span>' : ''}
                            </span>
                            <span class="text-[10px] text-gray-500 mt-0.5">${date}</span>
                        </div>
                        
                        <!-- Action Buttons -->
                        <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            ${canEdit ? `
                                <button class="p-1.5 text-gray-500 hover:text-blue-400 transition-colors btn-edit rounded-full hover:bg-zinc-800 cursor-pointer" title="수정">
                                    <span class="material-symbols-outlined text-sm">edit</span>
                                </button>
                            ` : ''}
                            ${canDelete ? `
                                <button class="p-1.5 text-gray-500 hover:text-red-400 transition-colors btn-delete rounded-full hover:bg-zinc-800 cursor-pointer" title="삭제">
                                    <span class="material-symbols-outlined text-sm">close</span>
                                </button>
                            ` : ''}
                            <button class="p-1.5 text-gray-500 hover:text-red-400 transition-colors btn-report rounded-full hover:bg-zinc-800 cursor-pointer" title="신고" style="${isMyComment ? 'display:none' : ''}">
                                <span class="material-symbols-outlined text-sm">flag</span>
                            </button>
                        </div>
                    </div>
                    
                    <!-- Content Area -->
                    <div class="comment-content-area mt-1">
                        <p class="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap font-medium break-words text-content">${data.content}</p>
                    </div>
                </div>
            </div>
        `;

        // 1. DELETE Logic
        if (canDelete) {
            const deleteBtn = outerDiv.querySelector('.btn-delete');
            if (deleteBtn) {
                deleteBtn.onclick = async () => {
                    if (await window.Modal.confirm(locale.t('comment.delete_confirm'), locale.t('common.delete'))) {
                        try {
                            await ContentService.deleteComment(data.id);
                            // Optimistic Removal
                            outerDiv.remove();
                            // Update Count
                            const countEl = container.querySelector('#comment-count');
                            if (countEl) {
                                const current = parseInt(countEl.innerText.replace(/\D/g, '')) || 0;
                                countEl.innerText = `(${Math.max(0, current - 1)})`;
                            }
                        } catch (e) {
                            console.error(e);
                            await window.Modal.alert(locale.t('comment.delete_error') + ": " + e.message);
                        }
                    }
                };
            }
        }

        // 2. EDIT Logic
        if (canEdit) {
            const editBtn = outerDiv.querySelector('.btn-edit');
            const contentArea = outerDiv.querySelector('.comment-content-area');
            const textContent = outerDiv.querySelector('.text-content');

            if (editBtn) {
                editBtn.onclick = () => {
                    const originalText = textContent.innerText;

                    contentArea.innerHTML = `
                        <div class="mt-2">
                            <textarea class="w-full bg-zinc-800 border border-zinc-600 rounded-lg p-2 text-sm text-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none" rows="2">${originalText}</textarea>
                            <div class="flex justify-end gap-2 mt-2">
                                <button class="px-3 py-1 text-xs text-gray-400 hover:bg-zinc-700 rounded-md transition-colors btn-cancel-edit">취소</button>
                                <button class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-500 transition-colors btn-save-edit">저장</button>
                            </div>
                        </div>
                    `;

                    const textarea = contentArea.querySelector('textarea');
                    textarea.focus();

                    // Cancel
                    contentArea.querySelector('.btn-cancel-edit').onclick = () => {
                        contentArea.innerHTML = '';
                        contentArea.appendChild(textContent);
                        textContent.innerText = originalText;
                    };

                    // Save
                    contentArea.querySelector('.btn-save-edit').onclick = async () => {
                        const newContent = textarea.value.trim();
                        if (!newContent) return window.Modal.alert(locale.t('comment.empty_error'));
                        if (newContent === originalText) {
                            contentArea.querySelector('.btn-cancel-edit').click();
                            return;
                        }

                        const saveBtn = contentArea.querySelector('.btn-save-edit');
                        saveBtn.disabled = true;
                        saveBtn.innerText = '저장 중...';

                        try {
                            await ContentService.updateComment(data.id, newContent);
                            contentArea.innerHTML = '';
                            contentArea.appendChild(textContent);
                            textContent.innerText = newContent;
                        } catch (e) {
                            console.error(e);
                            window.Modal.alert(locale.t('common.error') + ": " + e.message);
                            saveBtn.disabled = false;
                            saveBtn.innerText = '저장';
                        }
                    };
                };
            }
        }

        // 3. REPORT Logic
        const reportBtn = outerDiv.querySelector('.btn-report');
        if (reportBtn) {
            reportBtn.onclick = async () => {
                if (!window.user) return window.Modal.alert(locale.t('report.login_required'));

                // Dynamic Import
                const { ReportModal } = await import("../../modals/ReportModal.js");
                ReportModal.show(data.userId, data.id, 'comment');
            };
        }

        return outerDiv;
    };

    // Load initial comments
    loadComments();

    // Submit Logic
    submitBtn.onclick = async () => {
        const content = input.value.trim();
        if (!user) return;
        if (!content) return;

        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="loader-spin w-4 h-4 border-2 border-white/30 border-t-white inline-block"></span>';

        try {
            await ContentService.addComment({
                seriesId: safeSeriesId,
                episodeId: safeEpId,
                content,
                userId: user.uid,
                userEmail: user.email,
                photoURL: user.photoURL || null,
                displayName: user.displayName || user.email.split('@')[0],
            });

            input.value = '';
            input.style.height = 'auto';
            await loadComments();

        } catch (e) {
            console.error(e);
            await window.Modal.alert(locale.t('comment.submit_error') + ": " + e.message);
        } finally {
            submitBtn.disabled = false;
            submitBtn.innerText = '등록';
        }
    };
};
